"use client"

import { usePathname } from "next/navigation"
import { type ReactNode, useEffect, useRef } from "react"
import { type PageTransition, pageTransition } from "../lib/motion-contract.ts"

type BrowserMotionState = { pathname: string | null; returning: boolean }
// This state only records browser navigation. No user/session data crosses requests.
let browserMotion: BrowserMotionState | null = null

export const PageMotion = ({ children }: { readonly children: ReactNode }) => {
  const pathname = usePathname()
  const main = useRef<HTMLElement>(null)
  const currentTransition = useRef<{ path: string; transition: PageTransition } | null>(null)

  useEffect(() => {
    browserMotion ??= { pathname: null, returning: false }
    const markReturn = () => {
      if (browserMotion !== null) browserMotion.returning = browserMotion.pathname !== window.location.pathname
    }
    window.addEventListener("popstate", markReturn)
    return () => window.removeEventListener("popstate", markReturn)
  }, [])

  useEffect(() => {
    const element = main.current
    if (element === null || pathname === null) return
    browserMotion ??= { pathname: null, returning: false }
    const transition = currentTransition.current?.path === pathname ? currentTransition.current.transition : pageTransition({ previousPath: browserMotion.pathname, nextPath: pathname, historyReturn: browserMotion.returning, reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches })
    currentTransition.current = { path: pathname, transition }
    browserMotion.pathname = pathname
    browserMotion.returning = false
    element.dataset["pageTransition"] = "none"
    if (transition === "none") return
    const frame = window.requestAnimationFrame(() => {
      element.dataset["pageTransition"] = transition
    })
    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [pathname])

  // There is intentionally no pathname/search key: filtering never remounts children.
  return (
    <main className="main page-motion" data-page-transition="none" id="main-content" ref={main}>
      {children}
    </main>
  )
}
